import React from 'react';
import { Modal, Button, Snackbar, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { Body, Headline, ProgressBar, Title } from '@apptronik/react-exoskeleton';

export interface FileSizeEntry {
    size?: number;
    downloadProgress?: number;
    isDownloading?: boolean;
    error?: string | null;
}

export interface DownloadModalBaseProps<T> {
    open: boolean;
    setOpen: (open: boolean) => void;
    selected: T[];
    fileSizes: Record<string, FileSizeEntry>;
    setFileSizes: React.Dispatch<React.SetStateAction<Record<string, FileSizeEntry>>>;
    handleDownloadSelected: (selected: T[]) => void;
    getKey: (item: T) => string;
    getName: (item: T) => string;
    title: string;
    itemLabel: string;
}

export function DownloadModalBase<T>({
    open,
    setOpen,
    selected,
    fileSizes,
    setFileSizes,
    handleDownloadSelected,
    getKey,
    getName,
    title,
    itemLabel,
}: DownloadModalBaseProps<T>) {
    const sizeDivisor = 1024 * 1024; // Always use MB
    const totalSize = selected.reduce(
        (acc, item) => acc + (fileSizes[getKey(item)]?.size || 0),
        0
    ) / sizeDivisor;
    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            sx={{ display: 'flex', overflow: 'scroll', justifyContent: 'center', alignItems: 'center' }}
        >
            <div style={{ width: '50%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'white', padding: '20px', borderRadius: '8px' }}>
                    <h2>{title}</h2>
                    <p>{itemLabel}:</p>
                    <ul>
                        {selected.map((item) => {
                            const key = getKey(item);
                            const fileSize = fileSizes[key];
                            return (
                                <li key={key}>
                                    {getName(item)} - Size: {fileSize && fileSize.size ? `${(fileSize.size / sizeDivisor).toFixed(2)} MB` : 'Calculating...'}
                                </li>
                            )
                        })}
                    </ul>
                    <p>Total Size: {totalSize.toFixed(2)} MB</p>
                    <p>Are you sure you want to download the selected {itemLabel.toLowerCase()}?</p>
                    <Button onClick={() => { setOpen(false); setFileSizes({}); }}>Cancel</Button>
                    {' '}
                    <Button onClick={() => { handleDownloadSelected(selected); setOpen(false); }}>Download</Button>
                </Box>
            </div>
        </Modal>
    );
}

export interface ProgressSnackbarBaseProps<T> {
    open: boolean;
    setOpen: (open: boolean) => void;
    selected: T[];
    fileSizes: Record<string, FileSizeEntry>;
    setFileSizes: React.Dispatch<React.SetStateAction<Record<string, FileSizeEntry>>>;
    getKey: (item: T) => string;
    getName: (item: T) => string;
    headline: string;
}

export function ProgressSnackbarBase<T>({
    open,
    setOpen,
    selected,
    fileSizes,
    setFileSizes,
    getKey,
    getName,
    headline,
}: ProgressSnackbarBaseProps<T>) {
    return (
        <Snackbar
            open={open}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={(ev, reason) => {
                if (reason === 'clickaway') return;
                setOpen(false);
                setFileSizes({});
            }}
            message={
                <div style={{ width: '100%', minWidth: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', overflowY: 'auto' }}>
                    <div style={{ marginBottom: '1rem', position: 'relative', width: '100%' }}>
                        <IconButton
                            aria-label="close"
                            onClick={() => {
                                setOpen(false);
                                setFileSizes({});
                            }}
                            style={{ position: 'absolute', right: 14, top: 14 }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Headline variant="medium" style={{ color: '#CACACA' }}>{headline}</Headline>
                    </div>
                    {selected.map((item) => {
                        const key = getKey(item);
                        const fileSize = fileSizes[key];
                        if (!fileSize) return null;
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }} key={key}>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <Title variant="medium" style={{ color: '#8C8C8C' }}>{getName(item)}</Title>
                                    <Body variant="medium">{fileSize.downloadProgress}%</Body>
                                </div>
                                <ProgressBar key={key} value={fileSize.downloadProgress} color="#767676" />
                            </div>
                        );
                    })}
                </div>
            }
        />
    );
}
